<template>
  <div class="well-test-surface">
    <div class="input-with-options">
      <div class="body-2">Liquid Input</div>
      <div class="input-option-group">
        <template v-if="isHpsSingleFluidRateLabel">
          <div class="hps-single-fluid-rate-label">
            {{ hpsSingleFluidRateLabel }}
          </div>
          <div v-if="disableRadioButtons" class="hps-single-fluid-rate-options">
            <div
              v-for="option in wellTestLiquidInputOptions?.well_test_liquid_input_options"
              :key="`liquid-input-options-${option.value}`"
            >
              <template v-if="option.id !== 'productivity_index_water_cut'">
                <RadioButton
                  :disabled="true"
                  :input-id="`liquid-input-options-${option.value}`"
                  name="liquid-input-options"
                  :value="option.value"
                  :model-value="liquidInputType"
                />

                <label :for="`liquid-input-options-${option.value}`">
                  {{ option.comment }}
                </label>
              </template>
            </div>
          </div>
        </template>
        <template v-else>
          <div
            v-for="option in wellTestLiquidInputOptions?.well_test_liquid_input_options"
            :key="`liquid-input-options-${option.value}`"
          >
            <!--
              PI/Water Cut liquid input option is not available in HPS scenarios because
              the selected HPS fluid type drives the allowed liquid input mode.
            -->
            <template
              v-if="
                !isHps &&
                (form.data.value.__typename !== 'performance_evaluations' ||
                  option.id !== 'productivity_index_water_cut')
              "
            >
              <RadioButton
                :disabled="disableRadioButtons"
                :input-id="`liquid-input-options-${option.value}`"
                name="liquid-input-options"
                :value="option.value"
                :model-value="liquidInputType"
                @update:model-value="emit('update:liquid-input-type', $event)"
              />

              <label :for="`liquid-input-options-${option.value}`">
                {{ option.comment }}
              </label>
            </template>
            <template
              v-else-if="isHps && option.id !== 'productivity_index_water_cut'"
            >
              <RadioButton
                :disabled="disableRadioButtons || !isHpsLiquidInputEnabled"
                :input-id="`liquid-input-options-${option.value}`"
                name="liquid-input-options"
                :value="option.value"
                :model-value="liquidInputType"
                @update:model-value="emit('update:liquid-input-type', $event)"
              />

              <label :for="`liquid-input-options-${option.value}`">
                {{ option.comment }}
              </label>
            </template>
          </div>
        </template>
      </div>
      <div class="converter-group">
        <template
          v-if="
            liquidInputType ===
            Well_Test_Liquid_Input_Options_Enum.TotalRateWaterCut
          "
        >
          <ConverterInput
            :label="totalLiquidRateLabel"
            :base-unit="LiquidFlowRateUnits.BarrelsPerDay"
            :model="LiquidFlowRateUnits.options()"
            :decimal-places="7"
            mode="write"
            :update-unit="props.updateUnit"
            :model-value="totalLiquidFlowRateStbd"
            @update:model-value="
              emit('update:total-liquid-flow-rate-stbd', $event)
            "
          />
          <ConverterInput
            v-if="canShowWaterCutInput"
            label="Water Cut"
            :base-unit="PercentageUnits.Percentage"
            :model="PercentageUnits.options()"
            :decimal-places="4"
            mode="write"
            :update-unit="props.updateUnit"
            :model-value="waterCutPercentage"
            @update:model-value="emit('update:water-cut-percentage', $event)"
          />
          <ConverterInput
            v-if="!isHps"
            label="Well Productivity Index"
            :base-unit="ProductivityIndexUnits.BarrelDayPsi"
            :model="ProductivityIndexUnits.options()"
            :decimal-places="7"
            mode="convert"
            :update-unit="props.updateUnit"
            :model-value="wellProductivityIndexStbdPsiaForDisplay"
          />
        </template>
        <template
          v-else-if="
            liquidInputType ===
            Well_Test_Liquid_Input_Options_Enum.OilRateWaterRate
          "
        >
          <ConverterInput
            v-if="!isHpsWaterOrAmine"
            label="Oil Rate"
            :base-unit="LiquidFlowRateUnits.BarrelsPerDay"
            :model="LiquidFlowRateUnits.options()"
            :decimal-places="4"
            mode="write"
            :update-unit="props.updateUnit"
            :model-value="oilFlowRateStbd"
            @update:model-value="emit('update:oil-flow-rate-stbd', $event)"
          />
          <ConverterInput
            :label="isHpsAmine ? 'Amine Rate' : 'Water Rate'"
            :decimal-places="4"
            :base-unit="LiquidFlowRateUnits.BarrelsPerDay"
            :model="LiquidFlowRateUnits.options()"
            mode="write"
            :update-unit="props.updateUnit"
            :model-value="waterFlowRateStbd"
            @update:model-value="emit('update:water-flow-rate-stbd', $event)"
          />
          <ConverterInput
            v-if="!isHps"
            label="Productivity Index"
            :base-unit="ProductivityIndexUnits.BarrelDayPsi"
            :model="ProductivityIndexUnits.options()"
            :decimal-places="7"
            mode="convert"
            :update-unit="props.updateUnit"
            :model-value="wellProductivityIndexStbdPsiaForDisplay"
          />
        </template>
        <template
          v-else-if="
            liquidInputType ===
            Well_Test_Liquid_Input_Options_Enum.OilRateWaterCut
          "
        >
          <ConverterInput
            label="Oil Rate"
            :base-unit="LiquidFlowRateUnits.BarrelsPerDay"
            :model="LiquidFlowRateUnits.options()"
            :decimal-places="4"
            mode="write"
            :update-unit="props.updateUnit"
            :model-value="oilFlowRateStbd"
            @update:model-value="emit('update:oil-flow-rate-stbd', $event)"
          />
          <ConverterInput
            v-if="canShowWaterCutInput"
            label="Water Cut"
            :base-unit="PercentageUnits.Percentage"
            :model="PercentageUnits.options()"
            :decimal-places="4"
            mode="write"
            :update-unit="props.updateUnit"
            :model-value="waterCutPercentage"
            @update:model-value="emit('update:water-cut-percentage', $event)"
          />
          <ConverterInput
            v-if="!isHps"
            label="Productivity Index"
            :base-unit="ProductivityIndexUnits.BarrelDayPsi"
            :model="ProductivityIndexUnits.options()"
            :decimal-places="7"
            mode="convert"
            :update-unit="props.updateUnit"
            :model-value="wellProductivityIndexStbdPsiaForDisplay"
          />
        </template>
        <template
          v-else-if="
            !isHps &&
            liquidInputType ===
              Well_Test_Liquid_Input_Options_Enum.ProductivityIndexWaterCut
          "
        >
          <ConverterInput
            label="Productivity Index"
            :base-unit="ProductivityIndexUnits.BarrelDayPsi"
            :model="ProductivityIndexUnits.options()"
            :decimal-places="7"
            mode="write"
            :update-unit="props.updateUnit"
            :model-value="productivityIndexStbdPsia"
            @update:model-value="
              emit('update:productivity-index-stbd-psia', $event)
            "
          />
          <ConverterInput
            label="Water Cut"
            :base-unit="PercentageUnits.Percentage"
            :model="PercentageUnits.options()"
            :decimal-places="4"
            mode="write"
            :update-unit="props.updateUnit"
            :model-value="waterCutPercentage"
            @update:model-value="emit('update:water-cut-percentage', $event)"
          />
        </template>
      </div>
    </div>
    <div v-if="!isAmineOrWater" class="input-with-options">
      <div class="body-2">Gas Input</div>
      <div class="input-option-group">
        <div
          v-for="option in wellTestGasInputOptions?.well_test_gas_input_options"
          :key="`gas-input-options-${option.value}`"
        >
          <RadioButton
            :disabled="disableRadioButtons"
            :input-id="`gas-input-options-${option.value}`"
            name="gas-input-options"
            :value="option.value"
            :model-value="gasInputType"
            @update:model-value="emit('update:gas-input-type', $event)"
          />
          <label :for="`gas-input-options-${option.value}`">
            {{ option.comment }}
          </label>
        </div>
      </div>
      <div class="converter-group">
        <template
          v-if="gasInputType === Well_Test_Gas_Input_Options_Enum.GasOilRatio"
        >
          <ConverterInput
            label="Gas/Oil ratio"
            :base-unit="LiquidRatioUnits.StandardCubicFootPerStockTankBarrel"
            :model="LiquidRatioUnits.options()"
            :min="0"
            :decimal-places="4"
            mode="write"
            :update-unit="props.updateUnit"
            :model-value="gasOilRatioScfStb"
            @update:model-value="emit('update:gas-oil-ratio-scf-stb', $event)"
          />
        </template>
        <template
          v-else-if="
            gasInputType === Well_Test_Gas_Input_Options_Enum.GasLiquidRatio
          "
        >
          <ConverterInput
            label="Gas/Liquid ratio"
            :base-unit="LiquidRatioUnits.StandardCubicFootPerStockTankBarrel"
            :model="LiquidRatioUnits.options()"
            :min="0"
            :decimal-places="4"
            mode="write"
            :update-unit="props.updateUnit"
            :model-value="gasLiquidRatioScfStb"
            @update:model-value="
              emit('update:gas-liquid-ratio-scf-stb', $event)
            "
          />
        </template>
        <template
          v-else-if="gasInputType === Well_Test_Gas_Input_Options_Enum.GasRate"
        >
          <ConverterInput
            label="Gas Rate"
            :base-unit="GasFlowRateUnits.ThousandCubicFeetPerDay"
            :model="GasFlowRateUnits.options()"
            :decimal-places="4"
            mode="write"
            :update-unit="props.updateUnit"
            :model-value="gasFlowRateMscf"
            @update:model-value="emit('update:gas-flow-rate-mscf', $event)"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { FormItems } from "@/composables/forms/formTypes";

import {
  useWellTestGasInputOptionsQuery,
  useWellTestLiquidInputOptionsQuery,
  Well_Test_Liquid_Input_Options_Enum,
  Well_Test_Gas_Input_Options_Enum,
  Sizing_Pumping_Type_Enum,
  Hps_Liquid_Type_Enum
} from "gql/api";

import { isHpsLiquidTypeWithWaterCutInput } from "@/utils/hpsLiquidType";

import { GasFlowRateUnits, LiquidFlowRateUnits } from "@/libs/units/flowRate";
import { PercentageUnits } from "@/libs/units/percentage";
import { ProductivityIndexUnits } from "@/libs/units/productivityIndex";
import { LiquidRatioUnits } from "@/libs/units/liquidRatio";

import ConverterInput from "@/components/ConverterInput.vue";
import { computed, watch } from "vue";
import { injectStrict } from "@/utils/utils";
import {
  parametersFormKey,
  sizingFormKey,
  wellPropertiesFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";

const parametersForm = injectStrict(parametersFormKey);
const sizingForm = injectStrict(sizingFormKey, null);
const wellPropertiesForm = injectStrict(wellPropertiesFormKey);

const isAmineOrWater = computed(() => {
  const type = wellPropertiesForm.data.value?.hps_liquid_type;
  return (
    type === Hps_Liquid_Type_Enum.Amine || type === Hps_Liquid_Type_Enum.Water
  );
});

const isHpsAmine = computed(() => {
  return (
    isHps.value &&
    wellPropertiesForm.data.value?.hps_liquid_type ===
      Hps_Liquid_Type_Enum.Amine
  );
});

const isHpsCondensate = computed(() => {
  return (
    isHps.value &&
    wellPropertiesForm.data.value?.hps_liquid_type ===
      Hps_Liquid_Type_Enum.Condensate
  );
});

const totalLiquidRateLabel = computed(() => {
  return isHpsCondensate.value ? "Condensate Rate" : "Total Liquid Rate";
});

const isHpsSingleFluidRateLabel = computed(() => {
  if (!isHps.value) return false;

  const type = wellPropertiesForm.data.value?.hps_liquid_type;
  return (
    type === Hps_Liquid_Type_Enum.Water ||
    type === Hps_Liquid_Type_Enum.Oil ||
    type === Hps_Liquid_Type_Enum.Condensate ||
    type === Hps_Liquid_Type_Enum.Amine
  );
});

const hpsSingleFluidRateLabel = computed(() => {
  switch (wellPropertiesForm.data.value?.hps_liquid_type) {
    case Hps_Liquid_Type_Enum.Water:
      return "Water Rate";
    case Hps_Liquid_Type_Enum.Oil:
      return "Oil Rate";
    case Hps_Liquid_Type_Enum.Condensate:
      return "Condensate Rate";
    case Hps_Liquid_Type_Enum.Amine:
      return "Amine Rate";
    default:
      return "Rate";
  }
});

const isHpsWaterOrAmine = computed(() => {
  const type = wellPropertiesForm.data.value?.hps_liquid_type;
  return (
    isHps.value &&
    (type === Hps_Liquid_Type_Enum.Amine || type === Hps_Liquid_Type_Enum.Water)
  );
});

const canShowWaterCutInput = computed(() =>
  isHpsLiquidTypeWithWaterCutInput(
    wellPropertiesForm.data.value?.hps_liquid_type,
    isHps.value
  )
);

const isHpsLiquidInputEnabled = computed(() => {
  if (!isHps.value) return true;

  const type = wellPropertiesForm.data.value?.hps_liquid_type;
  return type === Hps_Liquid_Type_Enum.Multiphase;
});

const parameters = computed(() => parametersForm.data.value);

const { data: wellTestLiquidInputOptions } =
  useWellTestLiquidInputOptionsQuery();
const { data: wellTestGasInputOptions } = useWellTestGasInputOptionsQuery();

const props = defineProps<{
  totalLiquidFlowRateStbd: number;
  waterCutPercentage: number;
  oilFlowRateStbd: number;
  waterFlowRateStbd: number;
  productivityIndexStbdPsia: number;
  wellProductivityIndexStbdPsiaForDisplay: number;
  gasOilRatioScfStb: number;
  gasLiquidRatioScfStb: number;
  gasFlowRateMscf: number;
  disableRadioButtons?: boolean;
  liquidInputType?: string | null;
  gasInputType?: string | null;
  updateUnit?: boolean;
  form: FormItems["performanceEvaluation"] | FormItems["wellProperties"];
}>();

const emit = defineEmits<{
  (e: "update:total-liquid-flow-rate-stbd", value: number | null): void;
  (e: "update:water-cut-percentage", value: number | null): void;
  (e: "update:oil-flow-rate-stbd", value: number | null): void;
  (e: "update:water-flow-rate-stbd", value: number | null): void;
  (e: "update:productivity-index-stbd-psia", value: number | null): void;
  (e: "update:gas-oil-ratio-scf-stb", value: number | null): void;
  (e: "update:gas-liquid-ratio-scf-stb", value: number | null): void;
  (e: "update:gas-flow-rate-mscf", value: number | null): void;
  (
    e: "update:liquid-input-type",
    value: Well_Test_Liquid_Input_Options_Enum
  ): void;
  (e: "update:gas-input-type", value: Well_Test_Gas_Input_Options_Enum): void;
}>();

const totalLiquidFlowComputed = computed(() => {
  return props.totalLiquidFlowRateStbd;
});

const isEspOnShore = computed(() => {
  return (
    sizingForm?.data.value.pumping_type === Sizing_Pumping_Type_Enum.EspOnShore
  );
});

const isHps = computed(() => {
  return sizingForm?.data.value.pumping_type === Sizing_Pumping_Type_Enum.Hps;
});

// If it is an HPS scenario, the well test liquid rate is the same as the target liquid rate
// so we need to update the target liquid rate when the total liquid flow changes
watch([isEspOnShore, totalLiquidFlowComputed], ([espOnShore, totalLiq]) => {
  if (
    !espOnShore &&
    Number.isFinite(totalLiq) &&
    totalLiq !== parameters.value.target_liquid_rate_std_barrel_day
  ) {
    parametersForm.update({
      target_liquid_rate_std_barrel_day: totalLiq ?? null
    });
  }
});
</script>

<style lang="scss">
.well-test-surface {
  display: grid;
  grid-template-columns: 1fr;
  column-gap: 1em;

  @include bp("touch") {
    grid-template-columns: 1fr 1fr;
  }

  @include bp("hd") {
    grid-template-columns: auto auto;
    column-gap: 2em;
  }

  .input-with-options {
    flex: 1;
    display: grid;
    grid-template-rows: 16px 1fr 1fr;

    .input-option-group {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 1em;
      margin-top: 1em;
      margin-bottom: 1em;

      @include bp("desktop") {
        flex-direction: row;
        flex-wrap: wrap;
      }

      > div {
        display: flex;
        gap: 0.25em;
      }

      .hps-single-fluid-rate-label {
        display: flex;
        align-items: center;
        font-weight: 600;
      }

      .hps-single-fluid-rate-options {
        display: flex;
        align-items: center;

        > div {
          display: flex;
          gap: 0.25em;
        }
      }
    }

    .converter-group {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 0.5em;

      @include bp("hd") {
        flex-direction: row;
      }
    }
  }
}
</style>
